import { useMemo, useCallback } from 'react';
import { Extension } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';

type ImportStatement = {
  full: string;
  type: 'react' | 'external' | 'internal' | 'relative' | 'type';
  path: string;
  isDefault: boolean;
  isNamed: boolean;
  isTypeOnly: boolean;
  startLine: number;
  endLine: number;
};

// Parse import statements from code
function parseImports(code: string): ImportStatement[] {
  const imports: ImportStatement[] = [];
  const lines = code.split('\n');

  let inMultilineImport = false;
  let currentImport = '';
  let startLine = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this is the start of an import
    if (line.trim().startsWith('import ')) {
      if (line.includes(';') || (line.includes("'") && line.lastIndexOf("'") > line.indexOf("'"))) {
        // Single line import
        const parsed = parseImportLine(line.trim(), i, i);
        if (parsed) imports.push(parsed);
      } else {
        // Start of multiline import
        inMultilineImport = true;
        currentImport = line;
        startLine = i;
      }
    } else if (inMultilineImport) {
      currentImport += '\n' + line;
      if (line.includes(';') || line.trim().endsWith("';") || line.trim().endsWith('";')) {
        // End of multiline import
        const parsed = parseImportLine(currentImport.trim(), startLine, i);
        if (parsed) imports.push(parsed);
        inMultilineImport = false;
        currentImport = '';
      }
    }
  }

  return imports;
}

// Parse a single import statement
function parseImportLine(line: string, startLine: number, endLine: number): ImportStatement | null {
  // Match import path
  const pathMatch = line.match(/from\s+['"]([^'"]+)['"]/);
  if (!pathMatch) {
    // Could be side-effect import like: import 'styles.css'
    const sideEffectMatch = line.match(/import\s+['"]([^'"]+)['"]/);
    if (sideEffectMatch) {
      return {
        full: line,
        type: categorizeImport(sideEffectMatch[1]),
        path: sideEffectMatch[1],
        isDefault: false,
        isNamed: false,
        isTypeOnly: false,
        startLine,
        endLine,
      };
    }
    return null;
  }

  const path = pathMatch[1];
  const isTypeOnly = line.includes('import type ');
  const isDefault = /import\s+(?:type\s+)?(\w+)(?:\s*,|\s+from)/.test(line) && !line.includes('{');
  const isNamed = line.includes('{');

  return {
    full: line,
    type: isTypeOnly ? 'type' : categorizeImport(path),
    path,
    isDefault,
    isNamed,
    isTypeOnly,
    startLine,
    endLine,
  };
}

// Categorize import by path
function categorizeImport(path: string): ImportStatement['type'] {
  if (path === 'react' || path.startsWith('react/') || path.startsWith('react-')) {
    return 'react';
  }
  if (path.startsWith('.') || path.startsWith('/')) {
    return 'relative';
  }
  if (path.startsWith('@/') || path.startsWith('~/')) {
    return 'internal';
  }
  return 'external';
}

// Sort imports
function sortImports(imports: ImportStatement[]): ImportStatement[] {
  // Group by type
  const groups: Record<ImportStatement['type'], ImportStatement[]> = {
    react: [],
    external: [],
    internal: [],
    relative: [],
    type: [],
  };

  for (const imp of imports) {
    groups[imp.type].push(imp);
  }

  // Sort each group alphabetically by path
  for (const type of Object.keys(groups) as ImportStatement['type'][]) {
    groups[type].sort((a, b) => a.path.localeCompare(b.path));
  }

  // Combine in order: react, external, internal, relative, type
  return [
    ...groups.react,
    ...groups.external,
    ...groups.internal,
    ...groups.relative,
    ...groups.type,
  ];
}

// Find unused imports by checking if identifiers are used in the code
function findUnusedImports(code: string, imports: ImportStatement[]): Set<string> {
  const unused = new Set<string>();

  // Get the code without imports
  const codeWithoutImports = code.split('\n').filter((line, i) => {
    return !imports.some((imp) => i >= imp.startLine && i <= imp.endLine);
  }).join('\n');

  for (const imp of imports) {
    // Extract imported identifiers
    const identifiers: string[] = [];

    // Default import: import Name from '...'
    const defaultMatch = imp.full.match(/import\s+(?:type\s+)?(\w+)(?:\s*,|\s+from)/);
    if (defaultMatch && !imp.full.includes('{')) {
      identifiers.push(defaultMatch[1]);
    }

    // Named imports: import { Name, Other } from '...'
    const namedMatch = imp.full.match(/\{([^}]+)\}/);
    if (namedMatch) {
      const names = namedMatch[1].split(',').map((n) => {
        // Handle "Name as Alias" and "type Name"
        const trimmed = n.trim();
        if (trimmed.includes(' as ')) {
          return trimmed.split(' as ')[1].trim();
        }
        if (trimmed.startsWith('type ')) {
          return trimmed.slice(5).trim();
        }
        return trimmed;
      });
      identifiers.push(...names);
    }

    // Check if each identifier is used
    for (const id of identifiers) {
      if (!id) continue;
      // Create a regex that matches the identifier as a whole word
      const regex = new RegExp(`\\b${id}\\b`);
      if (!regex.test(codeWithoutImports)) {
        unused.add(id);
      }
    }
  }

  return unused;
}

// Remove unused imports from an import statement
function removeUnusedFromImport(imp: ImportStatement, unused: Set<string>): ImportStatement | null {
  let newFull = imp.full;

  // Check if this is a side-effect import (no identifiers)
  if (!imp.isDefault && !imp.isNamed) {
    return imp; // Keep side-effect imports
  }

  // Handle named imports
  if (imp.isNamed) {
    const namedMatch = imp.full.match(/\{([^}]+)\}/);
    if (namedMatch) {
      const names = namedMatch[1].split(',').map((n) => n.trim()).filter((n) => {
        // Extract the actual name (handle "as" aliases and "type")
        let name = n;
        if (name.includes(' as ')) {
          name = name.split(' as ')[1].trim();
        }
        if (name.startsWith('type ')) {
          name = name.slice(5).trim();
        }
        return !unused.has(name);
      });

      if (names.length === 0) {
        // All named imports are unused
        if (imp.isDefault) {
          // Keep default import, remove named
          newFull = newFull.replace(/,?\s*\{[^}]+\}/, '');
        } else {
          // No imports left
          return null;
        }
      } else {
        newFull = newFull.replace(/\{[^}]+\}/, `{ ${names.join(', ')} }`);
      }
    }
  }

  // Handle default import
  if (imp.isDefault) {
    const defaultMatch = imp.full.match(/import\s+(?:type\s+)?(\w+)(?:\s*,|\s+from)/);
    if (defaultMatch && unused.has(defaultMatch[1])) {
      if (imp.isNamed && newFull.includes('{')) {
        // Keep named imports, remove default
        newFull = newFull.replace(/import\s+(?:type\s+)?\w+\s*,\s*/, 'import ');
      } else {
        // No imports left
        return null;
      }
    }
  }

  return { ...imp, full: newFull };
}

// Organize imports command
function organizeImports(view: EditorView): boolean {
  const code = view.state.doc.toString();
  const imports = parseImports(code);

  if (imports.length === 0) return false;

  // Find unused imports
  const unused = findUnusedImports(code, imports);

  // Remove unused and sort
  const cleanedImports = imports
    .map((imp) => removeUnusedFromImport(imp, unused))
    .filter((imp): imp is ImportStatement => imp !== null);

  const sortedImports = sortImports(cleanedImports);

  // Build new import block
  let newImportBlock = '';
  let prevType: ImportStatement['type'] | null = null;

  for (const imp of sortedImports) {
    // Add blank line between groups
    if (prevType !== null && prevType !== imp.type) {
      newImportBlock += '\n';
    }
    newImportBlock += imp.full + '\n';
    prevType = imp.type;
  }

  // Find the range to replace (from first import to last import)
  const firstImport = imports[0];
  const lastImport = imports[imports.length - 1];
  const lines = code.split('\n');

  let from = 0;
  for (let i = 0; i < firstImport.startLine; i++) {
    from += lines[i].length + 1;
  }

  let to = 0;
  for (let i = 0; i <= lastImport.endLine; i++) {
    to += lines[i].length + 1;
  }

  // Apply the change
  view.dispatch({
    changes: { from, to, insert: newImportBlock },
  });

  return true;
}

export function useOrganizeImports() {
  const organizeImportsExtension = useMemo<Extension>(() => {
    return keymap.of([
      {
        // Cmd+Shift+O is already used for Go to Symbol, use Alt+Shift+O
        key: 'Alt-Shift-o',
        run: organizeImports,
      },
    ]);
  }, []);

  // Export the command for use in command palette
  const organizeImportsCommand = useCallback((view: EditorView) => {
    return organizeImports(view);
  }, []);

  return {
    organizeImportsExtension,
    organizeImportsCommand,
    organizeImportsFn: organizeImports,
  };
}

// Export for command palette
export const organizeImportsCommands = [
  { name: 'Organize Imports', fn: organizeImports },
];
